import { CheckCircle2, Loader2, Circle } from "lucide-react";
import type { DnsStatusResponse } from "@/lib/api";

// OnboardingTimeline renders the wizard's progress as a vertical timeline.
// Source-of-truth is the /dns-status response — every milestone has its own
// timestamp column so we can show "when" each step happened, not just
// "whether". Items past the current point read as `done` (green check),
// the next pending item as `active` (animated spinner), and items beyond
// it stay grey.
//
// Lives in two places:
//   - Right-hand drawer of OnboardingShell (always visible while
//     authenticated, gives the user a sense of how far they are)
//   - Wizard terminal page (StepComplete), where it replaces the older
//     three-line static checklist
export interface TimelineItem {
  key: string;
  label: string;
  detail?: string;
  at?: string | null; // ISO timestamp; null/undefined → not yet
  // overridden when the timeline runs in "wizard step" mode (where steps 1-4
  // are intrinsic to the local store, not derived from the backend response).
  done?: boolean;
}

export function OnboardingTimeline({
  items,
  emptyHint,
}: {
  items: TimelineItem[];
  emptyHint?: string;
}) {
  // Find the first not-yet-done item — that's the active one.
  const activeIdx = items.findIndex((it) => !isDone(it));
  return (
    <ol className="space-y-4">
      {items.map((it, i) => {
        const done = isDone(it);
        const active = !done && i === activeIdx;
        return (
          <li key={it.key} className="flex items-start gap-3 text-sm">
            <Marker state={done ? "done" : active ? "active" : "pending"} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span
                  className={
                    done
                      ? "text-foreground"
                      : active
                      ? "text-foreground font-medium"
                      : "text-muted-foreground"
                  }
                >
                  {it.label}
                </span>
              </div>
              {it.detail && (
                <p className="mt-0.5 font-mono text-[11px] text-muted-foreground truncate">
                  {it.detail}
                </p>
              )}
              {done && it.at && (
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {formatTimestamp(it.at)}
                </p>
              )}
            </div>
          </li>
        );
      })}
      {items.length === 0 && emptyHint && (
        <li className="text-xs text-muted-foreground">{emptyHint}</li>
      )}
    </ol>
  );
}

function isDone(it: TimelineItem): boolean {
  if (typeof it.done === "boolean") return it.done;
  return !!it.at;
}

function Marker({ state }: { state: "done" | "active" | "pending" }) {
  const cls =
    "mt-[2px] inline-flex h-5 w-5 items-center justify-center rounded-full shrink-0 ";
  if (state === "done") {
    return (
      <span className={cls + "bg-signal-ok/20 text-signal-ok"}>
        <CheckCircle2 className="h-3 w-3" />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span className={cls + "bg-brand-violet/20 text-brand-violet"}>
        <Loader2 className="h-3 w-3 animate-spin" />
      </span>
    );
  }
  return (
    <span className={cls + "bg-muted text-muted-foreground"}>
      <Circle className="h-3 w-3" />
    </span>
  );
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// itemsFromDnsStatus derives the post-subdomain section of the timeline from
// the backend's DnsStatusResponse. Used by both the drawer and StepComplete.
export function itemsFromDnsStatus(
  status: DnsStatusResponse | null,
  subdomain: string | null,
): TimelineItem[] {
  return [
    {
      key: "subdomain_reserved",
      label: "Subdomain reserved",
      detail: subdomain ? `${subdomain}.virtualdesk.ai` : undefined,
      at: status?.cname_created_at ?? null,
    },
    {
      key: "cname_created",
      label: "DNS record created at Netlify",
      at: status?.cname_created_at ?? null,
    },
    {
      key: "domain_alias_claimed",
      label: "Tenant hostname claimed on edge",
      at: status?.domain_alias_claimed_at ?? null,
    },
    {
      key: "cname_resolved",
      label: "Subdomain resolves on the public internet",
      at: status?.cname_resolved_at ?? null,
    },
    {
      key: "https_reachable",
      label: "Workspace reachable over HTTPS",
      at: status?.https_reachable_at ?? null,
    },
    {
      key: "subdomain_ready",
      label: '"Workspace ready" email sent',
      at: status?.subdomain_ready_at ?? null,
    },
  ];
}
